import { chartColors } from '@shared/lib/chart-theme';
import {
  formatCount,
  formatPercent,
  isPaying,
  usageStateOf,
  type UsageState,
  type UserEngagement,
} from '@features/engagement/model/engagement.types';

/** Ordered worst-to-best, and coloured from the same ramp as the ladder. */
const SEGMENTS: Array<{ state: UsageState; label: string; colour: string }> = [
  { state: 'never', label: 'Never used it', colour: chartColors.ordinal[0] as string },
  { state: 'quiet', label: 'Gone quiet', colour: chartColors.ordinal[2] as string },
  { state: 'using', label: 'Using it', colour: chartColors.ordinal[4] as string },
];

/**
 * Whether the people paying are the people using it.
 *
 * Two counts side by side never answered this — "23 pay" and "9 use it free"
 * are the same question asked twice, and the reader had to do the crossing
 * themselves. As two bars split the same way, the mismatch is the picture: the
 * paying bar is mostly the colour of people who never used the product.
 *
 * Crossed here rather than on the server: every row already carries its plan
 * price and its rung, so this is a tally, not a query.
 */
export function PayVsUseChart({ users }: { users: UserEngagement[] }) {
  const rows = [
    { label: 'Paying customers', members: users.filter(isPaying) },
    { label: 'Free, lapsed or never subscribed', members: users.filter((u) => !isPaying(u)) },
  ];

  return (
    <div>
      <div className="space-y-3">
        {rows.map((row) => {
          const total = row.members.length;
          const counts = SEGMENTS.map((segment) => ({
            ...segment,
            count: row.members.filter((user) => usageStateOf(user) === segment.state).length,
          }));

          return (
            <div key={row.label}>
              <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-slate-700">{row.label}</span>
                <span className="shrink-0 font-semibold tabular-nums text-slate-900">
                  {formatCount(total)}
                </span>
              </div>

              {total === 0 ? (
                <div className="h-3 rounded-full bg-slate-100" />
              ) : (
                // A 2px gap between fills rather than a border around them.
                <div
                  className="flex h-3 gap-0.5"
                  role="img"
                  aria-label={counts
                    .map((segment) => `${segment.label}: ${segment.count}`)
                    .join(', ')}
                >
                  {counts
                    .filter((segment) => segment.count > 0)
                    .map((segment, index, kept) => (
                      <div
                        key={segment.state}
                        title={`${segment.label}: ${formatCount(segment.count)}`}
                        style={{
                          width: `${(segment.count / total) * 100}%`,
                          backgroundColor: segment.colour,
                        }}
                        className={[
                          'h-full',
                          index === 0 ? 'rounded-l-full' : '',
                          index === kept.length - 1 ? 'rounded-r-full' : '',
                        ].join(' ')}
                      />
                    ))}
                </div>
              )}

              <p className="mt-1 text-xs text-slate-400">
                {counts
                  .filter((segment) => segment.count > 0)
                  .map(
                    (segment) =>
                      `${formatCount(segment.count)} ${segment.label.toLowerCase()} (${formatPercent(
                        segment.count / total,
                      )})`,
                  )
                  .join(' · ') || 'nobody yet'}
              </p>
            </div>
          );
        })}
      </div>

      {/* Identity is never colour alone: every segment is named here and in the
          caption under its bar. */}
      <ul className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 pt-2.5">
        {SEGMENTS.map((segment) => (
          <li key={segment.state} className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <span
              aria-hidden
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: segment.colour }}
            />
            {segment.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
